import { openDB } from 'idb';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { api } from '../api';

async function initDB() {
  return openDB('cacheDB', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('responses')) {
        db.createObjectStore('responses', { keyPath: 'cacheKey' });
      }
    },
  });
}

// Define an interface for options with optional properties
interface RequestOptions extends AxiosRequestConfig {}

// In-memory tracker for ongoing fetches
const pendingRequests: Map<string, Promise<any>> = new Map();

export async function cachedRequest(
  url: string,
  options: RequestOptions = {},
  cacheKey: string,
  cacheDuration: number = 300000,
): Promise<any> {
  const db = await initDB();
  const now = Date.now();

  // Check for cached response
  const cached = await db.get('responses', cacheKey);
  if (cached && now - cached.timestamp < cacheDuration) {
    return cached.data;
  }

  // If a request for the same cacheKey is already in progress, return that promise
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)!;
  }

  // If not, initiate the request and store the promise
  const requestPromise = (async () => {
    try {
      const response: AxiosResponse = await api(url, options);
      const responseData = response.data;

      await db.put('responses', {
        cacheKey,
        data: responseData,
        timestamp: Date.now(),
      });

      return responseData;
    } finally {
      // Clean up the pending request regardless of success or failure
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, requestPromise);

  return requestPromise;
}
